import { motion } from "framer-motion";
import { Flame, Martini, Utensils, UtensilsCrossed } from "lucide-react";
import { Reveal } from "./Reveal";
import { IMAGES, DISHES } from "../../data/site";

const CHAPTERS = [
  { n: "01", t: "The Room", d: "Low light, deep leather and a warm golden glow. Every corner is built to linger long into the evening." },
  { n: "02", t: "The Kitchen", d: "Authentic Ethiopian plates meet American favourites — berbere, injera, char-grilled and slow-simmered." },
  { n: "03", t: "The Table", d: "Ethiopian fire, premium cocktails and slow-burning hookah — shared the way a good night intends." },
];

const TILES = [
  { icon: Utensils, label: "Ethiopian Kitchen", img: DISHES[0].src, span: "md:col-span-2 md:row-span-2" },
  { icon: Flame, label: "Hookah", img: IMAGES.hookah, span: "" },
  { icon: Martini, label: "Great Cocktails", img: IMAGES.cocktail, span: "" },
  { icon: UtensilsCrossed, label: "Continental Plates", img: DISHES[5].src, span: "md:col-span-2" },
];

export const Experience = () => {
  return (
    <section id="experience" className="relative py-28 lg:py-40" data-testid="experience">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="overline mb-5">The Experience</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-[var(--br-cream)]">
            The Perfect Blend of <span className="italic text-[var(--br-gold)]">Ethiopian Flavor</span> &amp; Cocktails.
          </h2>
        </Reveal>

        {/* Numbered manifesto chapters */}
        <div className="mt-16 grid gap-10 md:grid-cols-3 border-t border-[var(--br-gold)]/15 pt-12">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12}>
              <div className="flex flex-col gap-3">
                <span className="font-display text-5xl text-[var(--br-gold)]/70">{c.n}</span>
                <h3 className="font-display text-2xl text-[var(--br-cream)]">{c.t}</h3>
                <p className="text-sm leading-relaxed text-[var(--br-muted)]">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Bento grid */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-4 auto-rows-[220px] gap-4">
          {TILES.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden rounded-3xl border border-[var(--br-gold)]/15 ${t.span}`}
              data-testid={`experience-tile-${i}`}
            >
              <img
                src={t.img}
                alt={t.label}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full border border-[var(--br-gold)]/50 text-[var(--br-gold)] bg-black/30 backdrop-blur">
                  <t.icon size={18} />
                </span>
                <span className="font-display text-2xl text-[var(--br-cream)]">{t.label}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
